import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SiteCard from "@/components/feature/SiteCard";
import CategorySidebar from "@/pages/category/components/CategorySidebar";
import { categories, getCategoryById } from "@/mocks/navCategories";
import { navSites } from "@/mocks/navSites";
import useFavorites from "@/hooks/useFavorites";

type SortKey = "recommend" | "name" | "newest";

const sortOptions: { id: SortKey; label: string }[] = [
  { id: "recommend", label: "推荐排序" },
  { id: "name", label: "按名称" },
  { id: "newest", label: "按新增时间" },
];

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryId = "" } = useParams();
  const category = getCategoryById(categoryId);
  const [sort, setSort] = useState<SortKey>("recommend");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [sortOpen, setSortOpen] = useState(false);
  const { isFavorite, toggle } = useFavorites();

  useEffect(() => {
    setSort("recommend");
    setVisible(PAGE_SIZE);
  }, [categoryId]);

  useEffect(() => {
    if (!category) {
      document.title = "分类不存在 - 51网址导航";
      return;
    }
    document.title = `${category.name}网站分类 - 51网址导航`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      `${category.name}分类汇总：${category.description} 51网址导航整理该领域常用网站，支持排序与快速访问。`,
    );
  }, [category]);

  const sites = useMemo(() => {
    const list = navSites.filter((site) => site.categoryId === categoryId);
    const sorted = [...list];
    if (sort === "name") {
      sorted.sort((a, b) => a.name.localeCompare(b.name, "zh-Hans-CN"));
    } else if (sort === "newest") {
      sorted.sort((a, b) => b.addedAt.localeCompare(a.addedAt));
    } else {
      sorted.sort((a, b) => Number(b.featured) - Number(a.featured) || b.visits - a.visits);
    }
    return sorted;
  }, [categoryId, sort]);

  const visibleSites = sites.slice(0, visible);
  const hasMore = visible < sites.length;

  if (!category) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 w-full px-4 md:px-6 py-20">
          <div className="mx-auto max-w-[560px] text-center rounded-lg bg-background-50 border border-background-200 p-10">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
              <i className="ri-folder-warning-line text-2xl" />
            </span>
            <h1 className="mt-4 font-heading font-bold text-xl text-foreground-950">
              该分类暂不存在
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              可能链接有误，你可以返回首页或选择其他分类继续浏览。
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {categories.slice(0, 4).map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="px-3 py-1.5 rounded-md bg-background-100 text-xs text-foreground-700 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/"
                className="px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-xs cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 w-full">
        <div className="w-full bg-background-100 border-b border-background-200">
          <div className="px-4 md:px-6 py-6 md:py-8">
            <nav className="text-xs text-foreground-500 flex items-center gap-1 flex-wrap">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-400">分类</span>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">{category.name}</span>
            </nav>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className={`${category.icon} text-xl`} />
              </span>
              <div>
                <h1 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                  {category.name}
                </h1>
                <p className="mt-1 text-xs md:text-sm text-foreground-500">
                  {category.description} 共收录 {sites.length} 个网站
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row gap-5 md:gap-6">
            <CategorySidebar activeId={categoryId} />

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-1.5">
                  {category.children.map((child) => {
                    const count = sites.filter((site) => site.subCategoryId === child.id).length;
                    return (
                      <span
                        key={child.id}
                        className="px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-700 whitespace-nowrap"
                      >
                        {child.name}
                        <span className="text-foreground-400 ml-1">{count}</span>
                      </span>
                    );
                  })}
                </div>

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setSortOpen((prev) => !prev)}
                    className="h-9 px-3 flex items-center gap-1.5 rounded-md bg-background-50 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-sort-desc text-base text-foreground-400" />
                    {sortOptions.find((item) => item.id === sort)?.label}
                    <i className={`ri-arrow-down-s-line transition-transform ${sortOpen ? "rotate-180" : ""}`} />
                  </button>
                  {sortOpen && (
                    <div className="absolute right-0 top-full mt-1 w-36 z-30 rounded-md bg-background-50 border border-background-200 p-1 animate-pop-in">
                      {sortOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            setSort(option.id);
                            setVisible(PAGE_SIZE);
                            setSortOpen(false);
                          }}
                          className={`w-full text-left px-2.5 py-2 rounded text-xs cursor-pointer ${
                            sort === option.id
                              ? "bg-primary-50 text-primary-700"
                              : "text-foreground-700 hover:bg-background-100"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {visibleSites.length === 0 ? (
                <div className="rounded-lg bg-background-50 border border-background-200 p-10 text-center">
                  <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-inbox-line text-2xl" />
                  </span>
                  <h2 className="mt-4 text-sm font-medium text-foreground-900">
                    暂无该分类网站
                  </h2>
                  <p className="mt-1.5 text-xs text-foreground-500">
                    您可以返回首页，或选择其他分类继续浏览。
                  </p>
                  <Link
                    to="/"
                    className="inline-flex mt-4 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-xs cursor-pointer whitespace-nowrap"
                  >
                    返回首页
                  </Link>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
                    {visibleSites.map((site) => (
                      <SiteCard
                        key={site.id}
                        site={site}
                        isFavorite={isFavorite(site.id)}
                        onToggleFavorite={toggle}
                      />
                    ))}
                  </div>

                  <div className="mt-6 flex flex-col items-center gap-2">
                    {hasMore ? (
                      <button
                        type="button"
                        onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                        className="w-full sm:w-auto px-6 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        加载更多（还有 {sites.length - visible} 个）
                      </button>
                    ) : (
                      <p className="text-xs text-foreground-400">
                        已经浏览完 {category.name} 分类的全部 {sites.length} 个网站
                      </p>
                    )}
                    <p className="text-[11px] text-foreground-400 text-center">
                      当前共 {visibleSites.length} 条记录 · 网址资料由编辑人工维护，前端数据为演示内容
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}